// Delad läsning av vilka dokument som är markerade som "ej aktuella" i projektet.
// Används av flikraden, processkartan, rollvyn och dokumenthuvudet så att
// lättläget slår igenom överallt i stället för bara på ett ställe.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  docTabMap,
  fetchInapplicableDocs,
  setStepApplicable,
  type InapplicableDoc,
} from "@/lib/basProcess/simpleMode";

export interface UseSimpleModeResult {
  /** Dokumentnummer → motivering (kan vara null). */
  reasons: Record<string, string | null>;
  /** Flikar som hör till enbart ej aktuella dokument. */
  hiddenTabs: Set<string>;
  count: number;
  loading: boolean;
  reload: () => Promise<void>;
  /** Gör ett dokument aktuellt igen. */
  restore: (docNumber: string) => Promise<void>;
}

const useSimpleMode = (projectId: string | undefined): UseSimpleModeResult => {
  const [docs, setDocs] = useState<InapplicableDoc[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!projectId) {
      setDocs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setDocs(await fetchInapplicableDocs(projectId));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const reasons = useMemo(() => {
    const map: Record<string, string | null> = {};
    docs.forEach((d) => {
      map[d.docNumber] = d.reason;
    });
    return map;
  }, [docs]);

  const hiddenTabs = useMemo(() => {
    const tabs = docTabMap();
    const inactive = new Set(docs.map((d) => d.docNumber));
    // En flik göms bara om alla dokument som pekar på fliken är ej aktuella.
    const byTab = new Map<string, string[]>();
    Object.entries(tabs).forEach(([doc, tab]) => {
      byTab.set(tab, [...(byTab.get(tab) ?? []), doc]);
    });
    const hidden = new Set<string>();
    byTab.forEach((docNumbers, tab) => {
      if (docNumbers.every((d) => inactive.has(d))) hidden.add(tab);
    });
    return hidden;
  }, [docs]);

  const restore = useCallback(
    async (docNumber: string) => {
      if (!projectId) return;
      await setStepApplicable(projectId, docNumber, true);
      await reload();
    },
    [projectId, reload]
  );

  return { reasons, hiddenTabs, count: docs.length, loading, reload, restore };
};

export default useSimpleMode;
